package diana.soleil.design.iraniancanadienbazzar.core.model

/**
 * Canonical `categoryId` → Material Symbols icon name for the directory taxonomy.
 *
 * Mirrors the `iconKey` of each category in `firebase/seed/businesses.firebase.json` (the canonical
 * taxonomy documented in `docs/DATA_MODEL.md`). Business rows show this when they have no cover photo,
 * so a photoless restaurant shows a fork/knife, a clinic shows a med-bag, etc. — instead of every
 * listing falling back to the generic storefront.
 *
 * A [Category] carries its own [Category.iconKey], so category tiles read it directly; this map is for
 * the reverse lookup from a [Business.categoryId] where the loaded category list isn't in scope. Keep
 * it in sync with the seed if the taxonomy changes. Unknown ids fall back to [CATEGORY_ICON_FALLBACK].
 */
private val CATEGORY_ICON_KEYS: Map<String, String> = mapOf(
    "food_restaurants" to "restaurant",
    "medical_health" to "medical_services",
    "beauty_wellness" to "spa",
    "legal_services" to "gavel",
    "finance_accounting" to "account_balance",
    "real_estate" to "home_work",
    "education_tutors" to "school",
    "auto_services" to "directions_car",
    "home_services" to "construction",
    "grocery_specialty_food" to "shopping_cart",
    "events_entertainment" to "celebration",
    "shopping" to "shopping_bag",
    "community_services" to "diversity_3",
    "moving_storage" to "local_shipping",
    "technology_services" to "devices",
)

/** Neutral fallback when a business's category id isn't in the canonical taxonomy. */
const val CATEGORY_ICON_FALLBACK: String = "storefront"

/** The Material Symbols icon name for [categoryId], or [CATEGORY_ICON_FALLBACK] if unknown/blank. */
fun categoryIconKey(categoryId: String?): String =
    CATEGORY_ICON_KEYS[categoryId] ?: CATEGORY_ICON_FALLBACK
